import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Wrench, AlertTriangle } from 'lucide-react';
import MachineServiceDialog from '../../components/MachineServiceDialog';

const Machines = () => {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [serviceMachine, setServiceMachine] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    machine_type: '',
    registration_no: '',
    hourly_rate: '',
  });

  useEffect(() => {
    fetchMachines();
    fetchUpcoming();
  }, []);

  const fetchUpcoming = async () => {
    try {
      const res = await api.upcomingMachineService();
      setUpcoming(res.data);
    } catch { /* ignore */ }
  };

  const fetchMachines = async () => {
    try {
      const response = await api.getMachines();
      setMachines(response.data);
    } catch (error) {
      console.error('Failed to fetch machines:', error);
      toast.error('Kunde inte hämta maskiner');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
      };
      await api.createMachine(data);
      toast.success('Maskin tillagd!');
      setDialogOpen(false);
      setFormData({ name: '', machine_type: '', registration_no: '', hourly_rate: '' });
      fetchMachines();
    } catch (error) {
      console.error('Failed to create machine:', error);
      toast.error('Kunde inte lägga till maskin');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker?')) return;
    try {
      await api.deleteMachine(id);
      toast.success('Maskin borttagen');
      fetchMachines();
    } catch (error) {
      console.error('Failed to delete machine:', error);
      toast.error('Kunde inte ta bort maskin');
    }
  };

  if (loading) {
    return <div>Laddar...</div>;
  }

  return (
    <div className="space-y-6" data-testid="machines-page">
      <div className="flex justify-end">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-machine-btn" className="bg-orange-500 hover:bg-orange-600">
              <Plus size={20} className="mr-2" />
              Ny maskin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Lägg till maskin</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Namn *</Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="machine-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="machine_type">Typ</Label>
                <Input
                  id="machine_type"
                  value={formData.machine_type}
                  onChange={(e) => setFormData({ ...formData, machine_type: e.target.value })}
                  data-testid="machine-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="registration_no">Registreringsnummer</Label>
                <Input
                  id="registration_no"
                  value={formData.registration_no}
                  onChange={(e) => setFormData({ ...formData, registration_no: e.target.value })}
                  data-testid="machine-regno-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hourly_rate">Timtaxa (kr)</Label>
                <Input
                  id="hourly_rate"
                  type="number"
                  step="0.01"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  data-testid="machine-rate-input"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Avbryt</Button>
                <Button type="submit" data-testid="create-machine-submit-btn" className="bg-orange-500 hover:bg-orange-600">Lägg till</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border">
        {upcoming.length > 0 && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-900 px-6 py-3 flex items-center gap-3" data-testid="upcoming-service-banner">
            <AlertTriangle size={16} className="text-amber-700 dark:text-amber-300 flex-shrink-0" />
            <p className="text-xs text-amber-900 dark:text-amber-200">
              <span className="font-bold">{upcoming.length}</span> service-poster förfaller inom 45 dagar — AI påminner inför varje förfallodag.
            </p>
          </div>
        )}
        {machines.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">Inga maskiner tillagda</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Namn</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Typ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Reg.nr</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timtaxa</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Åtgärd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {machines.map((machine) => {
                  const machineUpcoming = upcoming.filter(u => u.machine_id === machine.id);
                  return (
                  <tr key={machine.id}>
                    <td className="px-6 py-4 text-sm font-medium text-foreground">{machine.name}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{machine.machine_type || '-'}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{machine.registration_no || '-'}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {machine.hourly_rate ? `${machine.hourly_rate} kr` : '-'}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => setServiceMachine(machine)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[#E8613A] hover:text-[#D4532F]"
                        data-testid={`service-btn-${machine.id}`}
                      >
                        <Wrench size={13} /> {machineUpcoming.length > 0 ? `${machineUpcoming.length} kommande` : 'Hantera'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <button
                        onClick={() => handleDelete(machine.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {serviceMachine && (
        <MachineServiceDialog
          machine={serviceMachine}
          open={true}
          onClose={() => setServiceMachine(null)}
          onChanged={fetchUpcoming}
        />
      )}
    </div>
  );
};

export default Machines;
